import {
  dateSchema,
  objectIdSchema,
  paginationSchema,
  ReferralReward,
  referralRewardStatusSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/* ─────────────────────────── admin ─────────────────────────── */

/** `GET /referral/reward/admin-all` — the payout ledger. */
export const referralRewardGetAllAdminAPISchema = paginationSchema.extend({
  partnerId: objectIdSchema.optional().describe('Filter to one partner.'),
  linkId: objectIdSchema.optional().describe('Filter to one link.'),
  status: referralRewardStatusSchema
    .optional()
    .describe('Filter by `ReferralReward.status` (`pending` / `granted` / `void`).'),
  referredId: objectIdSchema
    .optional()
    .describe('Filter to rewards earned on this referred account (`referred._id`).'),
  leadId: objectIdSchema.optional().describe('Filter to rewards traced to this lead.'),
  from: dateSchema.optional().describe('`doc.createdAt >=`.'),
  to: dateSchema.optional().describe('`doc.createdAt <=`.'),
});
export type ReferralRewardGetAllAdminAPI = API<
  z.infer<typeof referralRewardGetAllAdminAPISchema>,
  ReferralReward[]
>;

/** `GET /referral/reward/admin-one` — one reward row. */
export const referralRewardGetOneAdminAPISchema = z.object({
  _id: objectIdSchema.describe('`ReferralReward._id`.'),
});
export type ReferralRewardGetOneAdminAPI = API<
  z.infer<typeof referralRewardGetOneAdminAPISchema>,
  ReferralReward
>;

/**
 * `POST /referral/reward/admin-mark-paid` — finance settled the cash
 * parts off-platform. Flips every `pending` cash part to `granted`
 * and, when nothing is left pending, the row to `granted`.
 */
export const referralRewardMarkPaidAdminAPISchema = z.object({
  _id: objectIdSchema.describe('`ReferralReward._id`.'),
  note: z
    .string()
    .trim()
    .optional()
    .describe('Payout reference — bank transfer id, date, who approved.'),
});
export type ReferralRewardMarkPaidAdminAPI = API<
  z.infer<typeof referralRewardMarkPaidAdminAPISchema>,
  ReferralReward
>;

/**
 * `POST /referral/reward/admin-void` — cancel a reward. Credits already
 * granted are not clawed back; the row is marked `void` with the reason.
 */
export const referralRewardVoidAdminAPISchema = z.object({
  _id: objectIdSchema.describe('`ReferralReward._id`.'),
  reason: z
    .string()
    .trim()
    .min(1)
    .describe('Why the reward is cancelled (refund, fraud, duplicate, …).'),
});
export type ReferralRewardVoidAdminAPI = API<
  z.infer<typeof referralRewardVoidAdminAPISchema>,
  ReferralReward
>;

/**
 * `POST /referral/reward/admin-relink-lead` — set or clear a lead's
 * referral attribution by hand (`mode: 'manual'`). Pass `linkId` to
 * attribute, omit it to unlink. Also re-stamps the lead's converted
 * account (`Org.meta.referral` / `User.meta.referral`) when one exists.
 */
export const referralRelinkLeadAdminAPISchema = z.object({
  leadId: objectIdSchema.describe('`Lead._id` to re-attribute.'),
  linkId: objectIdSchema
    .optional()
    .describe('`ReferralLink._id` to attribute the lead to. Omit to remove the attribution.'),
});
export type ReferralRelinkLeadAdminAPI = API<
  z.infer<typeof referralRelinkLeadAdminAPISchema>,
  { leadId: string; linkId?: string }
>;
